import { supertypeClass, property } from '@haventech/amorphic';
import { AuthenticatedAdminPrincipal } from './AuthenticatedAdminPrincipal';
import { FlexibleObj } from './interfaces/FlexibleObj';

@supertypeClass({ toClient: true, toServer: true })
export class Admin extends AuthenticatedAdminPrincipal {
  @property({ length: 40, rule: ['name', 'required'] })
  firstName: string;

  @property({ length: 40, rule: ['name'] })
  lastName: string;

  @property({ length: 50, rule: ['email', 'required'] })
  email: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordHash: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  passwordSalt: string;

  @property({ toServer: false })
  channels: string = '';

  @property({ toServer: false })
  role: string;

  @property({ sensitiveData: true })
  massMutualId: string;

  @property({ isLocal: true })
  roleValues: FlexibleObj<string> = {
    agent: 'Agent',
    adminagent: 'Super Agent',
    agentSupport: 'Agent Support',
    underwriter: 'Underwriter',
    marketing: 'Marketing',
    management: 'Management',
    auditor: 'Auditor',
    support: 'Support Team',
    sadmin: 'Security Admin',
  };

  constructor() {
    super();
  }

  static new(): Admin {
    return new Admin().init();
  }

  init(): this {
    super.init();
    return this;
  }

  getFullName(): string {
    return this.firstName + ' ' + this.lastName;
  }

  passwordValidation(password?: string): void {
    /*
         Contain at least two (2) of the following four (4) classes:
         • English upper case letters (e.g., A, B, C)
         • English lower case letters (e.g., a, b, c)
         • Numbers (e.g., 0, 1, 2)
         • Non-alphanumeric or “special characters” (e.g., ?,!,%,$,#)
         */
    let count = 0;

    if (this.role === 'sadmin') {
      /*
             Privileged account (resource administrator) passwords must consist of a minimum of ten (10)
             characters and at least three (3) of the previous four (4) classes.
             */
      count += password.replace(/[^A-Z]/g, '').length;
      count += password.replace(/[^a-z]/g, '').length;
      count += password.replace(/[^0-9]/g, '').length;
      count += password.replace(/[^?!%$#]/g, '').length;

      if (password.length < 10 || password.length > 30 || count < 3) {
        throw {
          code: 'password_composition',
          text: 'Password must be 10-30 characters with 3 occurrences of upper-case, lower-case, numbers and/or special characters ?,!,%,$,#',
        };
      }
    } else {
      /*
             Contain at least two (2) of the following four (4) classes:
             • English upper case letters (e.g., A, B, C)
             • English lower case letters (e.g., a, b, c)
             • Numbers (e.g., 0, 1, 2)
             • Non-alphanumeric or “special characters” (e.g., ?,!,%,$,#)
             */
      count += password.replace(/[^A-Z]/g, '').length;
      count += password.replace(/[^a-z]/g, '').length;
      count += password.replace(/[^0-9]/g, '').length;
      count += password.replace(/[^?!%$#]/g, '').length;

      if (password.length < 8 || password.length > 30 || count < 2) {
        throw {
          code: 'password_composition',
          text: 'Password must be 8-30 characters with at least one lowercase letter, uppercase letter, number, and special character (? ! % $ # & + - _ * @).',
        };
      }
    }
  }
}
